/**
 * Session storage utility with expiring keys.
 * Items expire after 15 minutes of inactivity.
 *
 * The SESSION_ID key has special handling: when it expires, all sap_ session
 * storage keys are cleared and a `sessionKeyExpired` custom DOM event is
 * dispatched so the UI can redirect the user back to the start of the survey or
 * to a Terms of Use page via passed in redirect url.
 */

export const SESSION_PREFIX = "sap_";
const EXPIRY_SUFFIX = "_expiry";
const EXPIRY_TIME = 15 * 60 * 1000; // 15 minutes in milliseconds
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Place all items to be temporarily stored on the frontend here
export const sessionItems = {
  SESSION_ID: "sessionId",
  QUIZ_ANSWERS: "quizAnswers",
  CURRENT_QUESTION: "currentQuestion",
  CURRENT_INDEX: "currentIndex",
  PAGE_STATE: "pageState",
  QUESTIONS: "questions",
  ASSESSMENT_RESULT: "assessmentResult",
  INFERENCE_ID: "inferenceId",
  EXPERIMENT_ASSIGNMENTS: "experimentAssignments",
  FRIENDLY_CAPTCHA_SOLUTION: "friendlyCaptchaSolution",
  FRIENDLY_CAPTCHA_VERIFIED_SESSION_ID: "friendlyCaptchaVerifiedSessionId",
  TERMS_OF_USE: "termsOfUse",
} as const;

export type SessionItem = (typeof sessionItems)[keyof typeof sessionItems];

export const sessionStorageExpiring = {
  set: (key: SessionItem, value: unknown): void => {
    if (typeof window === "undefined") return;

    try {
      sessionStorage.setItem(SESSION_PREFIX + key, JSON.stringify(value));
      updateExpiration(key);
    } catch (error) {
      console.error("Error setting session storage: ", error);
    }
  },

  get: <T>(key: SessionItem): T | null => {
    if (typeof window === "undefined") return null;

    if (key === sessionItems.SESSION_ID) {
      return handleSessionId() as T | null;
    }

    if (isKeyExpired(key)) {
      sessionStorageExpiring.remove(key);
      // Dispatch a custom event for key expiration
      window.dispatchEvent(
        new CustomEvent("sessionKeyExpired", { detail: { key } }),
      );
      return null;
    }

    const value = sessionStorage.getItem(SESSION_PREFIX + key);
    if (!value) return null;

    try {
      return JSON.parse(value) as T;
    } catch (error) {
      console.error("Error parsing session storage value: ", error);
      return null;
    }
  },

  remove: (key: SessionItem): void => {
    if (typeof window === "undefined") return;

    sessionStorage.removeItem(SESSION_PREFIX + key);
    sessionStorage.removeItem(SESSION_PREFIX + key + EXPIRY_SUFFIX);
  },

  clear: (): void => {
    if (typeof window === "undefined") return;

    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(SESSION_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  },

  refreshExpiry: (key: SessionItem): void => {
    if (typeof window === "undefined") return;

    if (sessionStorage.getItem(SESSION_PREFIX + key)) {
      updateExpiration(key);
    }
  },

  refreshAllExpiry: (): void => {
    Object.values(sessionItems).forEach((key) => {
      sessionStorageExpiring.refreshExpiry(key);
    });
  },
};

// When session expires, clear all sap_ keys and dispatch sessionKeyExpired event
const handleSessionId = (): string => {
  const value = sessionStorage.getItem(
    SESSION_PREFIX + sessionItems.SESSION_ID,
  );

  if (isKeyExpired(sessionItems.SESSION_ID)) {
    sessionStorageExpiring.clear();
    const newSessionId = generateSessionId();

    window.dispatchEvent(
      new CustomEvent("sessionKeyExpired", {
        detail: { key: sessionItems.SESSION_ID },
      }),
    );
    return newSessionId;
  }

  if (!value) {
    return generateSessionId();
  }
  try {
    return JSON.parse(value);
  } catch {
    return generateSessionId();
  }
};

/**
 * Generate a cryptographically-random RFC 4122 v4 UUID and persist it as the session ID.
 */
const generateSessionId = (): string => {
  const newSessionId = "10000000-1000-4000-8000-100000000000".replace(
    /[018]/g,
    (c) =>
      (
        +c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
      ).toString(16),
  );
  sessionStorageExpiring.set(sessionItems.SESSION_ID, newSessionId);
  return newSessionId;
};

/**
 * Accept a session ID from an external source (e.g. the page embedding the
 * widget). Validates that the value is a valid UUID v4 before storing.
 */
export const setExternalSessionId = (sessionId: string): void => {
  if (!UUID_REGEX.test(sessionId)) {
    console.warn(
      "Invalid session ID received, generating a new one:",
      sessionId,
    );
    generateSessionId();
    return;
  }
  sessionStorageExpiring.set(sessionItems.SESSION_ID, sessionId);
};

export const isKeyExpired = (key: SessionItem): boolean => {
  if (typeof window === "undefined") return false;

  const expiryTime = sessionStorage.getItem(
    SESSION_PREFIX + key + EXPIRY_SUFFIX,
  );
  return expiryTime ? Date.now() > Number(expiryTime) : false;
};

export const updateExpiration = (key: SessionItem): void => {
  if (typeof window === "undefined") return;

  sessionStorage.setItem(
    SESSION_PREFIX + key + EXPIRY_SUFFIX,
    String(Date.now() + EXPIRY_TIME),
  );
};

// ─── Quiz timing ──────────────────────────────────────────────────────────────
// Stored separately from the expiring session items so that expiry events are
// not triggered when reading the value mid-quiz.

const QUIZ_START_TS_KEY = `${SESSION_PREFIX}quizStartTs`;

/** Records the current timestamp as the quiz start time. Called on widget mount (sa-entry) to measure full session duration. */
export const setQuizStartTimestamp = (): void => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(QUIZ_START_TS_KEY, Date.now().toString());
  } catch (error) {
    console.error("Failed to persist quiz start timestamp:", error);
  }
};

/**
 * Returns elapsed seconds since setQuizStartTimestamp() was called, as a
 * string (to match the spec's saDuration type). Returns '0' if no start
 * timestamp is found.
 */
export const getQuizDurationSeconds = (): string => {
  if (typeof window === "undefined") return "0";
  try {
    const ts = sessionStorage.getItem(QUIZ_START_TS_KEY);
    if (!ts) return "0";
    const startTimestamp = parseInt(ts, 10);
    if (!Number.isFinite(startTimestamp)) return "0";
    return Math.round((Date.now() - startTimestamp) / 1000).toString();
  } catch {
    return "0";
  }
};
